import type { TemplateId } from '@cibli/shared';

interface Props {
  templateId: TemplateId;
  accentColor: string;
  isSelected: boolean;
}

/**
 * Minimal CSS-based skeleton thumbnail representing each template's layout structure.
 * Not a full preview — just enough to distinguish layouts at a glance.
 */
export default function TemplateSkeleton({ templateId, accentColor, isSelected }: Props) {
  const ringClass = isSelected
    ? 'ring-2 ring-offset-2'
    : 'ring-1 ring-gray-200 hover:ring-gray-300';

  return (
    <div
      className={`relative aspect-[210/297] w-full max-w-20 overflow-hidden rounded-md bg-white transition-all ${ringClass}`}
      style={
        isSelected
          ? ({
              '--tw-ring-color': accentColor,
              '--tw-ring-offset-color': '#fff',
            } as React.CSSProperties)
          : undefined
      }
    >
      {templateId === 'ciblijob' && <CompactSkeleton accentColor={accentColor} />}
      {templateId === 'classic' && <ClassicSkeleton />}
      {templateId === 'september' && <ModerneSkeleton accentColor={accentColor} />}
      {templateId === 'onepage' && <EleganceSkeleton accentColor={accentColor} />}
      {/* JSON Resume themes */}
      {templateId === 'json-elegant' && <JsonElegantSkeleton accentColor={accentColor} />}
      {templateId === 'json-flat' && <JsonFlatSkeleton />}
      {templateId === 'json-executive-slate' && (
        <JsonExecutiveSlateSkeleton accentColor={accentColor} />
      )}
      {templateId === 'json-modern-classic' && (
        <JsonModernClassicSkeleton accentColor={accentColor} />
      )}
      {templateId === 'json-stackoverflow' && (
        <JsonStackoverflowSkeleton accentColor={accentColor} />
      )}
    </div>
  );
}

function Bar({
  className = '',
  style,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}) {
  return (
    <div className={`rounded-sm ${className}`} style={style}>
      {children}
    </div>
  );
}

/* ========== Native templates ========== */

/** Compact: left colored sidebar + right content blocks */
function CompactSkeleton({ accentColor }: { accentColor: string }) {
  return (
    <div className="flex h-full">
      <div
        className="w-[27%] h-full flex flex-col gap-1 p-1.5"
        style={{ backgroundColor: accentColor }}
      >
        <Bar className="h-3 w-full bg-white/60" />
        <Bar className="h-2 w-3/4 bg-white/40" />
        <Bar className="mt-1 h-1.5 w-full bg-white/30" />
        <Bar className="h-1.5 w-5/6 bg-white/30" />
        <Bar className="h-1.5 w-full bg-white/30" />
        <Bar className="mt-1 h-1.5 w-2/3 bg-white/30" />
        <Bar className="h-1.5 w-3/4 bg-white/30" />
      </div>
      <div className="flex-1 p-2 flex flex-col gap-1">
        <Bar className="h-2.5 w-1/2 bg-gray-300" />
        <Bar className="h-1.5 w-1/3 bg-gray-200" />
        <Bar className="mt-1 h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-4/5 bg-gray-100" />
        <Bar className="mt-1 h-1.5 w-1/3 bg-gray-200" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-3/4 bg-gray-100" />
        <Bar className="mt-1 h-1.5 w-2/5 bg-gray-200" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-5/6 bg-gray-100" />
      </div>
    </div>
  );
}

/** Classic: gray left sidebar + white right content */
function ClassicSkeleton() {
  return (
    <div className="flex h-full">
      <div className="w-[33%] h-full bg-gray-100 flex flex-col gap-1 p-1.5">
        <Bar className="h-2 w-2/3 bg-gray-300" />
        <Bar className="h-1 w-full bg-gray-200" />
        <Bar className="h-1 w-4/5 bg-gray-200" />
        <Bar className="mt-0.5 h-2 w-1/2 bg-gray-300" />
        <Bar className="h-1 w-full bg-gray-200" />
        <Bar className="h-1 w-3/4 bg-gray-200" />
        <Bar className="mt-0.5 h-2 w-3/5 bg-gray-300" />
        <Bar className="h-1 w-full bg-gray-200" />
      </div>
      <div className="flex-1 p-1.5 flex flex-col gap-1">
        <Bar className="h-2 w-1/2 bg-gray-300" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-4/5 bg-gray-100" />
        <Bar className="mt-0.5 h-2 w-2/5 bg-gray-300" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-3/4 bg-gray-100" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-2/3 bg-gray-100" />
        <Bar className="mt-0.5 h-2 w-1/2 bg-gray-300" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-5/6 bg-gray-100" />
      </div>
    </div>
  );
}

/** Moderne: top colored stripe + 2-column body */
function ModerneSkeleton({ accentColor }: { accentColor: string }) {
  return (
    <div className="flex flex-col h-full">
      <div className="h-6 flex items-center px-2 gap-1" style={{ backgroundColor: accentColor }}>
        <Bar className="h-2.5 w-1/4 bg-white/70" />
        <Bar className="h-1.5 w-1/5 bg-white/50 ml-auto" />
      </div>
      <div className="flex gap-1.5 flex-1 p-2">
        <div className="w-[38%] flex flex-col gap-1">
          <Bar className="h-1.5 w-2/3 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-4/5 bg-gray-100" />
          <Bar className="mt-0.5 h-1.5 w-1/2 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-3/4 bg-gray-100" />
          <Bar className="mt-0.5 h-1.5 w-3/5 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <Bar className="h-1.5 w-1/2 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-4/5 bg-gray-100" />
          <Bar className="mt-0.5 h-1.5 w-2/5 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-3/4 bg-gray-100" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-2/3 bg-gray-100" />
        </div>
      </div>
    </div>
  );
}

/** Élégance: header with profile picture + name, then 2-column body */
function EleganceSkeleton({ accentColor }: { accentColor: string }) {
  return (
    <div className="flex flex-col h-full">
      <div
        className="flex items-center gap-1.5 px-1.5 pt-1 pb-0.5 border-b-2"
        style={{ borderColor: accentColor }}
      >
        <Bar
          className="h-3.5 w-3.5 rounded-full shrink-0"
          style={{ backgroundColor: accentColor }}
        />
        <div className="flex flex-col gap-0.5 min-w-0">
          <Bar className="h-2 w-full bg-gray-300" />
          <Bar className="h-1.5 w-2/3 bg-gray-200" />
        </div>
      </div>
      <div className="flex gap-1 flex-1 p-1.5">
        <div className="w-[33%] flex flex-col gap-1">
          <Bar className="h-1.5 w-2/3 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-4/5 bg-gray-100" />
          <Bar className="mt-0.5 h-1.5 w-1/2 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-3/4 bg-gray-100" />
          <Bar className="mt-0.5 h-1.5 w-3/5 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <Bar className="h-1.5 w-1/2 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-4/5 bg-gray-100" />
          <Bar className="mt-0.5 h-1.5 w-2/5 bg-gray-200" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-3/4 bg-gray-100" />
          <Bar className="h-1 w-full bg-gray-100" />
          <Bar className="h-1 w-2/3 bg-gray-100" />
        </div>
      </div>
    </div>
  );
}

/* ========== JSON Resume themes ========== */

/** json-elegant: profile banner (colored bg) at top + centered content below */
function JsonElegantSkeleton({ accentColor }: { accentColor: string }) {
  return (
    <div className="flex flex-col h-full">
      {/* Profile banner */}
      <div
        className="px-2 py-2 flex flex-col items-center gap-1"
        style={{ backgroundColor: `${accentColor}15` }}
      >
        <Bar className="h-4 w-4 rounded-full" style={{ backgroundColor: accentColor }} />
        <Bar className="h-2 w-3/4 bg-gray-300" />
        <Bar className="h-1 w-1/2 bg-gray-200" />
      </div>
      {/* Content sections */}
      <div className="flex-1 p-2 flex flex-col gap-1">
        <Bar className="h-1.5 w-1/3 bg-gray-300" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-4/5 bg-gray-100" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="mt-1 h-1.5 w-2/5 bg-gray-300" />
        <Bar className="flex justify-between">
          <span className="h-0.5 w-1/2 bg-gray-100" />
          <span className="h-0.5 w-1/4 bg-gray-100" />
        </Bar>
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-3/4 bg-gray-100" />
        <Bar className="mt-1 h-1.5 w-1/4 bg-gray-300" />
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-4/5 bg-gray-100" />
      </div>
    </div>
  );
}

/** json-flat: clean single column, minimal, no sidebar */
function JsonFlatSkeleton() {
  return (
    <div className="flex flex-col h-full p-2 gap-1">
      {/* Name + label */}
      <div className="flex flex-col items-start gap-0.5 pb-1 border-b border-gray-100">
        <Bar className="h-2.5 w-3/4 bg-gray-300" />
        <Bar className="h-1.5 w-1/2 bg-gray-200" />
      </div>
      {/* Contact row */}
      <div className="flex gap-2">
        <Bar className="h-1 w-1/4 bg-gray-100" />
        <Bar className="h-1 w-1/3 bg-gray-100" />
      </div>
      {/* Sections */}
      <Bar className="mt-1 h-1.5 w-1/3 bg-gray-300" />
      <Bar className="h-1 w-full bg-gray-100" />
      <Bar className="h-1 w-full bg-gray-100" />
      <Bar className="h-1 w-3/4 bg-gray-100" />
      <Bar className="mt-1 h-1.5 w-2/5 bg-gray-300" />
      <Bar className="flex justify-between">
        <span className="h-0.5 w-2/5 bg-gray-100" />
        <span className="h-0.5 w-1/4 bg-gray-100" />
      </Bar>
      <Bar className="h-1 w-full bg-gray-100" />
      <Bar className="h-1 w-4/5 bg-gray-100" />
      <Bar className="mt-1 h-1.5 w-1/3 bg-gray-300" />
      <Bar className="h-1 w-full bg-gray-100" />
      <Bar className="h-1 w-3/4 bg-gray-100" />
    </div>
  );
}

/** json-executive-slate: colored sidebar on left (dynamic accentColor) + white content right */
function JsonExecutiveSlateSkeleton({ accentColor }: { accentColor: string }) {
  return (
    <div className="flex h-full">
      {/* Sidebar matching dynamic accentColor */}
      <div
        className="w-[35%] flex flex-col items-center gap-1 p-1.5"
        style={{ backgroundColor: accentColor }}
      >
        {/* Centered avatar */}
        <div className="h-4 w-4 rounded-full bg-white/40 border border-white/30 shrink-0" />
        {/* Centered name & title */}
        <Bar className="h-2 w-4/5 bg-white/70" />
        <Bar className="h-1.5 w-3/5 bg-white/40 mb-1" />

        {/* Section: Skills (2 cols) */}
        <Bar className="h-1.5 w-full bg-white/50" />
        <div className="w-full grid grid-cols-2 gap-0.5 mb-1">
          <Bar className="h-1 bg-white/30" />
          <Bar className="h-1 bg-white/30" />
          <Bar className="h-1 bg-white/30" />
          <Bar className="h-1 bg-white/30" />
        </div>

        {/* Section: Education */}
        <Bar className="h-1.5 w-full bg-white/50" />
        <Bar className="h-1 w-full bg-white/30" />
        <Bar className="h-1 w-4/5 bg-white/30 mb-1" />

        {/* Section: Languages */}
        <Bar className="h-1.5 w-full bg-white/50" />
        <Bar className="flex justify-between w-full">
          <span className="h-1 w-2/5 bg-white/30" />
          <span className="h-1 w-1/4 bg-white/20" />
        </Bar>
        <Bar className="flex justify-between w-full mb-1">
          <span className="h-1 w-1/2 bg-white/30" />
          <span className="h-1 w-1/4 bg-white/20" />
        </Bar>

        {/* Section: Interests (3 cols) */}
        <Bar className="h-1.5 w-full bg-white/50" />
        <div className="w-full grid grid-cols-3 gap-0.5">
          <Bar className="h-1 bg-white/30" />
          <Bar className="h-1 bg-white/30" />
          <Bar className="h-1 bg-white/30" />
        </div>
      </div>
      {/* Main content */}
      <div className="flex-1 p-1.5 flex flex-col gap-1">
        {/* Summary */}
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-4/5 bg-gray-100 mb-1" />

        {/* Experiences */}
        <Bar className="h-1.5 w-1/2 bg-gray-300" />
        <Bar className="flex justify-between">
          <span className="h-1 w-2/5 bg-gray-200" />
          <span className="h-1 w-1/4 bg-gray-100" />
        </Bar>
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-5/6 bg-gray-100" />

        <Bar className="mt-1 flex justify-between">
          <span className="h-1 w-1/3 bg-gray-200" />
          <span className="h-1 w-1/4 bg-gray-100" />
        </Bar>
        <Bar className="h-1 w-full bg-gray-100" />
        <Bar className="h-1 w-3/4 bg-gray-100" />
      </div>
    </div>
  );
}

/** json-modern-classic: left text + right avatar header, single column with accent line titles */
function JsonModernClassicSkeleton({ accentColor }: { accentColor: string }) {
  return (
    <div className="flex flex-col h-full p-2 gap-1 bg-white">
      {/* Header: Left name/info + Right circular avatar */}
      <div className="flex justify-between items-start pb-1 border-b border-gray-200 gap-1">
        <div className="flex-1 flex flex-col gap-0.5">
          <Bar className="h-2.5 w-4/5 bg-gray-300" />
          <Bar className="h-1.5 w-1/2 bg-gray-200" />
          <Bar className="h-1 w-3/4 bg-gray-200" />
        </div>
        <div
          className="h-4 w-4 rounded-full bg-gray-200 shrink-0 border"
          style={{ borderColor: accentColor }}
        />
      </div>

      {/* Summary */}
      <Bar className="h-1 w-full bg-gray-100 mt-0.5" />
      <Bar className="h-1 w-4/5 bg-gray-100" />

      {/* Section: Experience with accent underline */}
      <div className="mt-0.5 pb-0.5 border-b" style={{ borderColor: accentColor }}>
        <Bar className="h-1.5 w-1/3 bg-gray-300" />
      </div>
      <Bar className="flex justify-between">
        <span className="h-1 w-2/5 bg-gray-200" />
        <span className="h-1 w-1/4 bg-gray-100" />
      </Bar>
      <Bar className="h-1 w-full bg-gray-100" />
      <Bar className="h-1 w-5/6 bg-gray-100" />

      {/* Section: Skills */}
      <div className="mt-0.5 pb-0.5 border-b" style={{ borderColor: accentColor }}>
        <Bar className="h-1.5 w-1/4 bg-gray-300" />
      </div>
      <div className="grid grid-cols-2 gap-1">
        <Bar className="h-2 bg-gray-100 border-l-2" style={{ borderColor: accentColor }} />
        <Bar className="h-2 bg-gray-100 border-l-2" style={{ borderColor: accentColor }} />
      </div>
    </div>
  );
}

/** json-stackoverflow: header with photo on right and contact/info on left, compact single column */
function JsonStackoverflowSkeleton({ accentColor }: { accentColor: string }) {
  return (
    <div className="flex flex-col h-full p-2 gap-1 bg-white">
      {/* Header: Left name/info + Right square avatar */}
      <div className="flex justify-between items-center pb-1 border-b border-gray-200 gap-1">
        <div className="flex-1 flex flex-col gap-0.5">
          <Bar className="h-2.5 w-4/5 bg-gray-300" />
          <Bar className="h-1.5 w-1/2 bg-gray-200" />
          <Bar className="h-1 w-3/4 bg-gray-100" />
          <div className="flex gap-1.5 mt-0.5">
            <Bar className="h-1 w-1/3 bg-gray-100" />
            <Bar className="h-1 w-1/4 bg-gray-100" />
          </div>
        </div>
        <div className="h-4 w-4 rounded bg-gray-200 shrink-0 border border-gray-100" />
      </div>

      {/* Summary */}
      <Bar className="h-1 w-full bg-gray-100 mt-0.5" />
      <Bar className="h-1 w-4/5 bg-gray-100" />

      {/* Section: Compétences (inline row) */}
      <div className="mt-0.5 pb-0.5 border-b" style={{ borderColor: accentColor }}>
        <Bar className="h-1.5 w-1/4" style={{ backgroundColor: accentColor }} />
      </div>
      <div className="flex flex-wrap gap-1">
        <Bar className="h-1.5 w-1/6 bg-gray-200 rounded-sm" />
        <Bar className="h-1.5 w-1/5 bg-gray-200 rounded-sm" />
        <Bar className="h-1.5 w-1/6 bg-gray-200 rounded-sm" />
        <Bar className="h-1.5 w-1/4 bg-gray-200 rounded-sm" />
      </div>

      {/* Section: Experience */}
      <div className="mt-0.5 pb-0.5 border-b" style={{ borderColor: accentColor }}>
        <Bar className="h-1.5 w-1/3" style={{ backgroundColor: accentColor }} />
      </div>
      <Bar className="flex justify-between">
        <span className="h-1 w-2/5 bg-gray-200" />
        <span className="h-1 w-1/4 bg-gray-100" />
      </Bar>
      <Bar className="h-1 w-full bg-gray-100" />
      <Bar className="h-1 w-5/6 bg-gray-100" />
    </div>
  );
}
